import React, {useEffect, useState} from 'react';
import "./profile.css";
import {Link, useParams} from "react-router";
import Spinner, {SpinnerSize} from "../components/spinner/Spinner";
import Map from "./Map";
import {countries} from "../countries";
import {CountryGroup} from "../defs/group";
import {ReactComponent as UN} from "../styles/un.svg";
import {Footer} from "../components/Footer";
import {collection, doc, Firestore, getDoc} from "firebase/firestore";
import {Wayfare} from "../defs/wayfare";
import {Session} from "../auth/session";

interface Params {
    username: string
}

interface Props {
    store: Firestore
}

function Profile({store}: Props) {
    const params = useParams() as unknown as Params;
    const wayfares = collection(store, "wayfares");
    const [profile, setProfile] = useState<Wayfare | null>(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        getDoc(doc(store, "wayfares", params.username.toLowerCase().replaceAll(/[^A-Za-z0-9]+/g, "")))
            .then(doc => {
                if (!doc.exists()) {
                    setNotFound(true);
                    return;
                }

                const profile: Wayfare = doc.data() as unknown as Wayfare;
                setProfile(profile);
                document.title = profile.name + "'s Wayfare | Wayf.re"
            })
    }, [params.username]);

    if (notFound)
        return <h2 className="text-center p-5 m-5">Profile not found</h2>

    if (!profile)
        return <div className="giant-loading">
            <Spinner size={SpinnerSize.SIZE_128}/>
        </div>

    const visited = profile.visited;
    const percentageVisited = function (group: CountryGroup, visited: string[]) {
        const total = countries.filter(country => country.group === group);
        const visitedInGroup = countries
            .filter(country => country.group === group)
            .filter(country => visited.indexOf(country.countryCode) > -1);
        return Math.round(visitedInGroup.length / total.length * 100);
    }

    const unMembersVisited = countries.filter(c => c.isUNMember)
        .filter(country => visited.indexOf(country.countryCode) > -1)
        .length;

    const countryList = function (group: CountryGroup, visited: string[]) {
        return <div className="country-group">
            <h2>
                {CountryGroup.displayName(group)}
                <small className="count">
                    {countries.filter(c => c.group === group).filter(c => visited.includes(c.countryCode)).length}
                    &nbsp;/&nbsp;
                    {countries.filter(c => c.group === group).length}
                </small>
            </h2>
            {countries
                .filter(country => country.group === group)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(country => <div key={country.countryCode} className={visited.indexOf(country.countryCode) > -1 ? "country-list country-visited" : "country-list country-not-visited"}>
                    <div className="flag-polyfill">
                        {country.flag}
                    </div>
                    <div className="country-name">
                        {country.name}
                    </div>
                    {country.isUNMember
                        ? <div className="country-un-marker">
                            <UN />
                        </div>
                        : false}
                </div>)}
        </div>
    }

    return <div id="app-root" className="fadein">
            <div className="intro">
                <h1>
                    <span>{profile.name}'s</span> Wayfare
                </h1>
            </div>
            <div className="map-container fadein">
                <Map visited={visited}/>
            </div>
            <div className="details">
                <div className="hero">
                    <div className="row">
                        <div className="col-12 col-lg-4">
                            <div className="summary-hero">
                                <div className="hero-1x">{visited.length}</div>
                                <div className="hero-sub">
                                    Countries<br/>
                                    and<br/>
                                    Territories<br/>
                                    Visited
                                </div>
                            </div>
                        </div>
                        <div className="col-6 col-lg-2">
                            <div className="hero-2x hero-2x hero-2x-special">{unMembersVisited}</div>
                            <div className="hero-2x-sub mb-2">
                                U.N. Members
                            </div>
                            <div className="hero-2x">{percentageVisited(CountryGroup.NORTH_AMERICA, visited)}%</div>
                            <div className="hero-2x-sub">
                                {CountryGroup.displayName(CountryGroup.NORTH_AMERICA)}
                            </div>
                        </div>
                        <div className="col-6 col-lg-2">
                            <div className="hero-2x">{percentageVisited(CountryGroup.CENTRAL_AMERICA, visited)}%</div>
                            <div className="hero-2x-sub mb-2">
                                {CountryGroup.displayName(CountryGroup.CENTRAL_AMERICA)}
                            </div>
                            <div className="hero-2x">{percentageVisited(CountryGroup.SOUTH_AMERICA, visited)}%</div>
                            <div className="hero-2x-sub">
                                {CountryGroup.displayName(CountryGroup.SOUTH_AMERICA)}
                            </div>
                        </div>
                        <div className="col-6 col-lg-2">
                            <div className="hero-2x">{percentageVisited(CountryGroup.EUROPE, visited)}%</div>
                            <div className="hero-2x-sub mb-2">
                                {CountryGroup.displayName(CountryGroup.EUROPE)}
                            </div>
                            <div className="hero-2x">{percentageVisited(CountryGroup.AFRICA, visited)}%</div>
                            <div className="hero-2x-sub">
                                {CountryGroup.displayName(CountryGroup.AFRICA)}
                            </div>
                        </div>
                        <div className="col-6 col-lg-2">
                            <div className="hero-2x">{percentageVisited(CountryGroup.ASIA, visited)}%</div>
                            <div className="hero-2x-sub mb-2">
                                {CountryGroup.displayName(CountryGroup.ASIA)}
                            </div>
                            <div className="hero-2x">{percentageVisited(CountryGroup.OCEANIA, visited)}%</div>
                            <div className="hero-2x-sub">
                                {CountryGroup.displayName(CountryGroup.OCEANIA)}
                            </div>
                        </div>
                    </div>
                </div>
                <hr/>
                <div className="row">
                    <div className="col-md-4">
                        {countryList(CountryGroup.NORTH_AMERICA, visited)}
                        {countryList(CountryGroup.EUROPE, visited)}
                    </div>
                    <div className="col-md-4">
                        {countryList(CountryGroup.CENTRAL_AMERICA, visited)}
                        {countryList(CountryGroup.SOUTH_AMERICA, visited)}
                        {countryList(CountryGroup.AFRICA, visited)}
                    </div>
                    <div className="col-md-4">
                        {countryList(CountryGroup.ASIA, visited)}
                        {countryList(CountryGroup.OCEANIA, visited)}
                    </div>
                </div>
                <hr/>
                <div className="text-center p-3">
                    <p>
                        Want to create your own travel map of your wayfare?
                    </p>
                    <Link to={"/my-wayfare"} className="btn btn-primary">Create Your Wayfare</Link>
                </div>
            </div>
            <Footer />
        </div>
}

export default Profile;
